import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, BACKEND_URL } from '@/App';
import PublicHeader from '@/components/PublicHeader';

const PricingPage = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (planId === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    setProcessingPlan(planId);

    try {
      const token = localStorage.getItem('token');
      const hostUrl = window.location.origin;
      
      const response = await fetch(`${API}/subscription/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: isYearly ? 'yearly' : 'monthly',
          host_url: hostUrl
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setProcessingPlan(null);
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Free',
      priceMonthly: 0,
      priceYearly: 0,
      description: 'Perfect for trying out TwentyDocs',
      features: [
        '10 documents per month',
        '10MB file size limit',
        'Basic templates',
        'Email support'
      ],
      popular: false,
      badge: null
    },
    {
      id: 'pro',
      name: 'Professional',
      priceMonthly: 34,
      priceYearly: 346.8,
      description: 'For growing teams and businesses',
      features: [
        'Unlimited documents',
        '10MB file size limit',
        'Advanced templates',
        'Conditional logic',
        'Priority support'
      ],
      popular: true,
      badge: null
    },
    {
      id: 'business',
      name: 'Business',
      priceMonthly: 49,
      priceYearly: 499.8,
      description: 'For large organizations',
      features: [
        'Unlimited documents',
        '25MB file size limit',
        'All Pro features',
        'Custom branding',
        'API access',
        'Dedicated support'
      ],
      popular: false,
      badge: null
    },
    {
      id: 'crm',
      name: 'CRM Professional',
      priceMonthly: 79,
      priceYearly: 806.4,
      description: 'Complete practice management for law firms',
      features: [
        'All Business features',
        'Client Management',
        'Case Management',
        'Firm Profile & Attorneys',
        'Opposing Counsel Directory',
        'Document Assembly with CRM Fields',
        'Case Calendar & Deadlines',
        '50MB file size limit',
        'Priority Support'
      ],
      popular: false,
      badge: 'CRM'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader breadcrumbs={[{ label: 'Pricing' }]} />

      {/* Pricing Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-8 sm:py-12 pt-24 sm:pt-32">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 px-4">Choose Your Plan</h1>
          <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 px-4">Select the perfect plan for your document automation needs</p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-2 sm:gap-4 p-1.5 sm:p-2 rounded-full bg-white border border-slate-200 shadow-sm">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-medium transition-all duration-300 text-sm sm:text-base ${
                !isYearly
                  ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="monthly-toggle"
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-medium transition-all duration-300 relative text-sm sm:text-base ${
                isYearly
                  ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="yearly-toggle"
            >
              Yearly
              <span className="absolute -top-1.5 sm:-top-2 -right-1.5 sm:-right-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-green-500 text-white text-[10px] sm:text-xs font-bold">
                Save 15%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-7xl mx-auto">
          {plans.map((plan) => {
            const price = isYearly ? plan.priceYearly : plan.priceMonthly;
            const isCurrentPlan = user?.subscription_plan === plan.id;
            const isProcessing = processingPlan === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative p-5 sm:p-6 rounded-2xl sm:rounded-3xl bg-white transition-all duration-300 ${
                  plan.popular
                    ? 'border-2 border-violet-500 shadow-2xl shadow-violet-200/50 sm:scale-105 z-10'
                    : plan.badge === 'CRM'
                    ? 'border-2 border-amber-400 shadow-xl shadow-amber-100/50'
                    : 'border-2 border-slate-200 hover:border-violet-500 shadow-xl'
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-block px-3 sm:px-4 py-1 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs sm:text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                {plan.badge === 'CRM' && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-block px-3 sm:px-4 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs sm:text-sm font-bold">
                      CRM Mode
                    </span>
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 sm:-top-4 right-3 sm:right-4">
                    <span className="inline-block px-2 sm:px-3 py-1 rounded-full bg-green-500 text-white text-[10px] sm:text-xs font-bold">
                      Current
                    </span>
                  </div>
                )}

                <div className="text-center mb-5 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-2 sm:mb-3">
                    <span className="text-3xl sm:text-4xl font-extrabold">${price}</span>
                    <span className="text-slate-600 ml-1 text-xs sm:text-sm">/ {isYearly ? 'year' : 'mo'}</span>
                  </div>
                  <p className="text-slate-600 text-xs sm:text-sm">{plan.description}</p>
                </div>

                <ul className="space-y-2 mb-5 sm:mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-violet-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-xs sm:text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={isCurrentPlan || isProcessing || loading}
                  className={`w-full h-10 sm:h-11 rounded-xl font-bold transition-all duration-300 text-sm sm:text-base ${
                    plan.popular
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl'
                      : plan.badge === 'CRM'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl'
                      : 'bg-white text-slate-900 border-2 border-slate-200 hover:border-violet-600 hover:text-violet-600'
                  } ${isCurrentPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                      <span>Processing...</span>
                    </div>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : plan.id === 'free' ? (
                    'Get Started Free'
                  ) : (
                    `Select ${plan.name.split(' ')[0]}`
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-12 sm:mt-16 md:mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 px-4">Frequently Asked Questions</h2>
          <div className="space-y-3 sm:space-y-4">
            <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-white border border-slate-200">
              <h3 className="font-bold text-base sm:text-lg mb-2">Can I switch plans anytime?</h3>
              <p className="text-slate-600 text-sm sm:text-base">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
            </div>
            <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-white border border-slate-200">
              <h3 className="font-bold text-base sm:text-lg mb-2">What happens to my documents if I downgrade?</h3>
              <p className="text-slate-600 text-sm sm:text-base">Your existing documents and templates remain accessible. However, you will be subject to the limits of your new plan for future documents.</p>
            </div>
            <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-white border border-slate-200">
              <h3 className="font-bold text-base sm:text-lg mb-2">Is there a free trial for paid plans?</h3>
              <p className="text-slate-600 text-sm sm:text-base">The Free plan serves as your trial. Upgrade anytime to unlock unlimited documents and advanced features.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;